import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys, { CamelCaseKeys } from 'camelcase-keys';

import { Database, Json, withLogs } from '@core';

import { apiClient, ApiConfig } from '../apiClient';
import { Club } from '../clubs/clubs';
import { Tag } from '../tags/tags';

export const ACTIVITY_QUERY = `
    *,
    club:clubs(*, club_types(*)),
    activity_tag_link(
        activity_tags(*)
        )
` as const;

type ConvertedActivityResponse = {
    endTime: string | null;
    id: number;
    activitySwingsSequence: number | null;
    boothId: number | null;
    calibrationId: number | null;
    clubId: number | null;
    layoutId: number | null;
    deleted: boolean | null;
    metadata: Json;
    startTime: string;
    swingInsightStatus: 'IN_PROGRESS' | 'COMPLETED' | null;
    userId: string;
    mode?: (typeof ACTIVITY_MODES)[keyof typeof ACTIVITY_MODES];
    lastLiveMeasurementAt: string | null;
    leftHanded: boolean;
    club: {
        clubTypeId: number;
        description: string | null;
        id: number;
        longName: string;
        shortName: string;
        clubTypes: {
            id: number;
            typeName: string;
        };
    } | null;
    activityTagLink: {
        activityTags: {
            createdAt: string;
            deleted: boolean;
            deletedAt: string | null;
            id: number;
            name: string;
            userId: string | null;
        };
    }[];
};

export const mapActivity = (activity: ConvertedActivityResponse): Activity => {
    const { activityTagLink, club, ...rest } = activity;

    return {
        ...rest,
        club: club ? { ...club, typeName: club.clubTypes.typeName ?? '' } : null,
        tags: activityTagLink.map((link) => link.activityTags),
    };
};

export const ACTIVITY_MODES = {
    DEFAULT: 'DEFAULT',
    BALLLESS: 'BALLLESS',
    LIVE_FEEDBACK: 'LIVE_FEEDBACK',
} as const;

export type Activity = {
    id: number;
    calibrationId: number | null;
    startTime: string;
    userId: string;
    layoutId: number | null;
    lastLiveMeasurementAt: string | null;
    leftHanded: boolean;
    mode?: (typeof ACTIVITY_MODES)[keyof typeof ACTIVITY_MODES];
    tags: Tag[];
    club: (Club & { typeName: string }) | null;
};

export const fetchActivityById = withLogs(
    async (supabaseClient: SupabaseClient<Database>, activityId: number): Promise<Activity | null> => {
        if (!activityId) return null;

        const { data, error } = await supabaseClient
            .from('activities')
            .select(ACTIVITY_QUERY)
            .eq('id', activityId)
            .single();

        if (error) {
            throw error;
        }

        const converted = camelcaseKeys(data, { deep: true });

        return mapActivity(converted);
    },
    'fetchActivityById',
);

export const markActivityAsDeleted = withLogs(async (activityId: number): Promise<number> => {
    if (!activityId && activityId < 1) {
        throw new Error(
            `Can not delete activity with no activity id, expected type of number got ${typeof activityId}, expected numeric value got ${activityId}`,
        );
    }

    const { status } = await apiClient.delete(ApiConfig.path(`api/v1/activities/${activityId}`));

    if (status !== 204) {
        throw Error('Deleted failed');
    }
    return activityId;
}, 'markActivityAsDeleted');

export const subscribeToActivity = (
    supabase: SupabaseClient<Database>,
    activityId: number,
    handler: (activity: CamelCaseKeys<Partial<Database['public']['Tables']['activities']['Row']>>) => void,
) => {
    return supabase
        .channel('activities')
        .on<Database['public']['Tables']['activities']['Row']>(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'activities',
                filter: `id=eq.${activityId}`,
            },
            (payload) => {
                try {
                    handler(camelcaseKeys(payload.new, { deep: true }));
                } catch (error) {
                    console.error('Error processing change:', error);
                }
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('user settings subscription error:', status);
            }
        });
};

export function patchCurrentActivity(mode: string) {
    return apiClient.patch<Activity>(ApiConfig.path('/api/v1/activities/current'), {
        mode,
    });
}
